import _ from 'lodash';
import type { MenuDish } from '../models';
import {
    defaultI18n,
    type DishItemI18nShape,
} from '../static/images/dish-base-data';

/** 新增菜品弹窗打开时菜品名称 1、名称 2 的默认占位 */
export const MENU_LIBRARY_DEFAULT_NEW_DISH_NAME = 'Unnamed';

/** 单个语言块内字段（与 PC `defaultI18n` 子结构一致） */
export type DishItemI18nBlock = {
    /** 对应 `item_name1` */
    name1: string;
    /** 对应 `item_name2` */
    name2: string;
    /** 对应 `item_description` */
    remark: string;
    /** 对应 `ext_dish_info` / 分量说明等 */
    extra_remark: string;
};

/** RBP 顶层语言键 + default */
export type DishItemI18nTopKey =
    | 'EN'
    | 'ZH'
    | 'IT'
    | 'ES'
    | 'FR'
    | 'DE'
    | 'PT'
    | 'SL'
    | 'CA'
    | 'default';

const TOP_KEYS: DishItemI18nTopKey[] = [
    'EN',
    'ZH',
    'IT',
    'ES',
    'FR',
    'DE',
    'PT',
    'SL',
    'CA',
    'default',
];

/** 空语言块 */
function emptyBlock(): DishItemI18nBlock {
    return { name1: '', name2: '', remark: '', extra_remark: '' };
}

/**
 * 全量默认骨架（各语言空串）。
 */
export function createEmptyDishItemI18nShape(): DishItemI18nShape {
    const o = {} as DishItemI18nShape;
    for (const k of TOP_KEYS) {
        o[k] = emptyBlock();
    }
    return o;
}

function buildInitialItemI18n(): DishItemI18nShape {
    const base = _.cloneDeep(defaultI18n);
    _.set(base, ['default', 'name1'], MENU_LIBRARY_DEFAULT_NEW_DISH_NAME);
    _.set(base, ['default', 'name2'], MENU_LIBRARY_DEFAULT_NEW_DISH_NAME);
    return base;
}

/**
 * 合并两个块（后者覆盖前者已有字段）。
 */
function mergeBlock(base: DishItemI18nBlock, patch?: Partial<DishItemI18nBlock>): DishItemI18nBlock {
    if (!patch) {
        return { ...base };
    }
    return {
        name1: patch.name1 ?? base.name1,
        name2: patch.name2 ?? base.name2,
        remark: patch.remark ?? base.remark,
        extra_remark: patch.extra_remark ?? base.extra_remark,
    };
}

/**
 * 将已解析的顶层对象（EN/ZH/default…）合并进默认骨架。
 * @param parsed - `JSON.parse` 或接口直接返回的对象
 */
function mergeItemI18nRecordIntoShape(
    parsed: Record<string, Partial<DishItemI18nBlock>>,
): DishItemI18nShape {
    const base = createEmptyDishItemI18nShape();
    const out = { ...base };
    for (const k of TOP_KEYS) {
        const patch = parsed[k];
        if (patch && typeof patch === 'object') {
            out[k] = mergeBlock(base[k], patch);
        }
    }
    return out;
}

/**
 * 判断 `item_i18n` 字段是否有可用内容（字符串非空或非空对象）。
 * @param raw - 接口上的 `item_i18n`
 */
function dishItemI18nFieldHasContent(raw: unknown): boolean {
    if (raw == null) {
        return false;
    }
    if (typeof raw === 'string') {
        const s = raw.trim();
        return s !== '' && s !== '{}' && s !== 'null';
    }
    if (typeof raw === 'object' && !Array.isArray(raw)) {
        return Object.keys(raw as object).length > 0;
    }
    return false;
}

/**
 * 将接口上的 `item_i18n`（JSON 字符串或已解析对象）转为合并形状；无有效内容时返回 null。
 * @param raw - `MenuDish.item_i18n` 运行时值
 */
export function parseDishItemI18nFromUnknown(raw: unknown): DishItemI18nShape | null {
    if (!dishItemI18nFieldHasContent(raw)) {
        return null;
    }
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw) as Record<string, Partial<DishItemI18nBlock>>;
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                return null;
            }
            return mergeItemI18nRecordIntoShape(parsed);
        } catch {
            return null;
        }
    }
    if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
        return mergeItemI18nRecordIntoShape(raw as Record<string, Partial<DishItemI18nBlock>>);
    }
    return null;
}

/**
 * 将主档 `item_name1` / `item_name2` 等写入 `default` 槽（与 RBP2 `classify-menu-detail` init 一致）。
 * @param merged - 已 parse 的 `item_i18n`
 * @param dish - 当前菜品主档字段
 */
export function syncDefaultFromMainFields(
    merged: DishItemI18nShape,
    dish: MenuDish,
): DishItemI18nShape {
    return {
        ...merged,
        default: {
            ...merged.default,
            name1: String(dish.item_name1 ?? ''),
            name2: String(dish.item_name2 ?? ''),
            remark: String(dish.item_description ?? ''),
            extra_remark: String(dish.ext_dish_info ?? ''),
        },
    };
}

/**
 * 从合并后的 `item_i18n` 与主档解析表单名称 1/2（空串时回退主档，再回退默认占位）。
 * @param merged - `buildItemI18nMergedFromDish` 结果
 * @param dish - 当前菜品
 */
export function resolveDishFormNamesFromHydrated(
    merged: DishItemI18nShape,
    dish: MenuDish,
): { name1: string; name2: string } {
    const name1 =
        String(merged.default.name1 ?? '').trim() ||
        String(dish.item_name1 ?? '').trim() ||
        MENU_LIBRARY_DEFAULT_NEW_DISH_NAME;
    const name2 =
        String(merged.default.name2 ?? '').trim() ||
        String(dish.item_name2 ?? '').trim() ||
        name1;
    return { name1, name2 };
}

/** 从已保存 `MenuDish` 合并 `item_i18n`，供编辑回填 */
export function buildItemI18nMergedFromDish(dish: MenuDish): DishItemI18nShape {
    const fromJson = parseDishItemI18nFromUnknown(dish?.item_i18n);
    if (fromJson != null) {
        return syncDefaultFromMainFields(fromJson, dish);
    }
    const base = buildInitialItemI18n();
    _.set(base, ['default', 'name1'], dish.item_name1 ?? MENU_LIBRARY_DEFAULT_NEW_DISH_NAME);
    _.set(base, ['default', 'name2'], dish.item_name2 ?? dish.item_name1 ?? MENU_LIBRARY_DEFAULT_NEW_DISH_NAME);
    _.set(base, ['default', 'remark'], dish.item_description ?? '');
    _.set(base, ['default', 'extra_remark'], dish.ext_dish_info ?? '');
    return base;
}
